<script setup>
import { computed } from 'vue'
const props = defineProps({
  inputValue: {
    type: [Number, String],
    required: true
  },
  conversionType: {
    type: String,
    required: true
  },
  calculatedResult: {
    type: [Number, String],
    required: true
  },
  targetUnit: {
    type: String,
    required: true
  }
})

const ranges = {
  'poor': { threshold: 183, color: 'red' },
  'fair': { threshold: 156, color: 'yellow' },
  'good': { threshold: 137, color: 'light-green' },
  'great': { threshold: 116, color: 'light-green' },
  'optimal': { threshold: 93, color: 'green' },
  'low': { threshold: 0, color: 'blue' }
}

const eag_value = computed(() => {
  if (props.conversionType === 'a1c_to_eag') {
    return props.targetUnit === 'mg_dl' ? props.calculatedResult : Math.ceil(props.calculatedResult * 18.01559)
  }
  else {
    return props.targetUnit === 'mg_dl' ? props.inputValue : Math.ceil(props.inputValue * 18.01559)
  }
})

const result_range = computed(() => {
  const eag = parseFloat(eag_value.value)
  for (const range in ranges) {
    if (eag > ranges[range].threshold) {
      return range
    }
  }
  return null
})
</script>

<template>
  <div class="results">
    <div class="result-primary">
      <div class="calc-hero_right-img w-embed">
        <svg width="100%" height="100%" viewBox="0 0 400 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            <!-- Define desaturation filter -->
            <defs>
                <filter id="desaturate">
                    <feColorMatrix type="saturate" values="0"></feColorMatrix>
                </filter>
            </defs>

            <!-- Poor: 144° to 180° (leftmost, pink) -->
            <path class="calculator calculator-poor" d="M 41.43 84.79 A 196 196 0 0 0 4 200 L 36 200 A 164 164 0 0 1 67.32 103.60 Z" fill="#FFDDF8" :filter="result_range !== 'poor' ? 'url(#desaturate)' : ''"></path>
            <path class="calculator calculator-poor" d="M 41.43 84.79 A 196 196 0 0 0 4 200 L 16 200 A 184 184 0 0 1 51.14 91.85 Z" fill="#FF68DE" :filter="result_range !== 'poor' ? 'url(#desaturate)' : ''"></path>

            <!-- Good: 108° to 144° (yellow) -->
            <path class="calculator calculator-good" d="M 139.43 13.59 A 196 196 0 0 0 41.43 84.79 L 67.32 103.60 A 164 164 0 0 1 149.32 44.03 Z" fill="#FBFBE7" :filter="result_range !== 'good' ? 'url(#desaturate)' : ''"></path>
            <path class="calculator calculator-good" d="M 139.43 13.59 A 196 196 0 0 0 41.43 84.79 L 51.14 91.85 A 184 184 0 0 1 143.14 25.00 Z" fill="#D7DB0E" :filter="result_range !== 'good' ? 'url(#desaturate)' : ''"></path>

            <!-- Great: 72° to 108° (top-center, light green) -->
            <path class="calculator calculator-great" d="M 260.57 13.59 A 196 196 0 0 0 139.43 13.59 L 149.32 44.03 A 164 164 0 0 1 250.68 44.03 Z" fill="#E6F9F1" :filter="result_range !== 'great' ? 'url(#desaturate)' : ''"></path>
            <path class="calculator calculator-great" d="M 260.57 13.59 A 196 196 0 0 0 139.43 13.59 L 143.14 25.00 A 184 184 0 0 1 256.86 25.00 Z" fill="#2ECC71" :filter="result_range !== 'great' ? 'url(#desaturate)' : ''"></path>
          
            <!-- Optimal: 36° to 72° (dark green) -->
            <path class="calculator calculator-optimal" d="M 358.57 84.79 A 196 196 0 0 0 260.57 13.59 L 250.68 44.03 A 164 164 0 0 1 332.68 103.60 Z" fill="#E6F9F1" :filter="result_range !== 'optimal' ? 'url(#desaturate)' : ''"></path>
            <path class="calculator calculator-optimal" d="M 358.57 84.79 A 196 196 0 0 0 260.57 13.59 L 256.86 25.00 A 184 184 0 0 1 348.86 91.85 Z" fill="#11C182" :filter="result_range !== 'optimal' ? 'url(#desaturate)' : ''"></path>

            <!-- Low: 0° to 36° (rightmost, blue) -->
            <path class="calculator calculator-low" d="M 396 200 A 196 196 0 0 0 358.57 84.79 L 332.68 103.60 A 164 164 0 0 1 364 200 Z" fill="#DBEEFF" :filter="result_range !== 'low' ? 'url(#desaturate)' : ''"></path>
            <path class="calculator calculator-low" d="M 396 200 A 196 196 0 0 0 358.57 84.79 L 348.86 91.85 A 184 184 0 0 1 384 200 Z" fill="#3498db" :filter="result_range !== 'low' ? 'url(#desaturate)' : ''"></path>
        </svg>
      </div>

      <div class="result-value-wrapper">
        <div class="typography">Result</div>
        <div class="result-value">
          <span class="animated-number">{{calculatedResult}}</span>
        </div>
        <div class="typography left-padded">
          {{ conversionType === 'a1c_to_eag' ? (targetUnit === 'mg_dl' ? ' mg/dL' : ' mmol/L') : ' %' }}
        </div>
      </div>
    </div>

    <div class="result-helptext">
      <div class="typography text-bold">Understanding your results</div>
    </div>

    <div class="threshold-list">
      <div class="threshold-item bg-red" v-if="eag_value > 183">
        <div class="threshold-text">
          <span class="label">Poor </span>
          <span class="value">&gt; 8.0% (184 mg/dL)</span>
        </div>
        <div class="description">
          Your A1C is in a range that suggests higher-than-ideal average blood sugar—an opportunity to refine daily habits and steadily move toward your target.
        </div>
      </div>
      <div class="threshold-item bg-yellow" v-else-if="eag_value >= 157">
        <div class="threshold-text">
          <span class="label">Fair </span>
          <span class="value">7.1% – 8.0% (157 - 183 mg/dL)</span>
        </div>
        <div class="description">
          Your estimated A1c is in a fair range—your recent blood-sugar levels look steady; keep what’s working and fine‑tune to move closer to optimal.
        </div>
      </div>
      <div class="threshold-item bg-light-green" v-else-if="eag_value >= 138">
        <div class="threshold-text">
          <span class="label">Good </span>
          <span class="value">6.4% – 7.0% (138 - 156 mg/dL)</span>
        </div>
        <div class="description">
          Your estimated A1c is in a good range, indicating steady glucose balance over the past few months—keep reinforcing the habits that support this consistency.
        </div>
      </div>
      <div class="threshold-item bg-light-green" v-else-if="eag_value >= 117">
        <div class="threshold-text">
          <span class="label">Great </span>
          <span class="value">5.7% – 6.4% (117 - 137 mg/dL)</span>
        </div>
        <div class="description">
          Your estimated A1c is in a great range, indicating steady glucose balance over the past few months—keep reinforcing the habits that support this consistency.
        </div>
      </div>
      <div class="threshold-item bg-green" v-else-if="eag_value >= 94">
        <div class="threshold-text">
          <span class="label">Optimal </span>
          <span class="value">4.9% – 5.7% (94 - 116 mg/dL)</span>
        </div>
        <div class="description">
          Your A1C sits in the optimal range—signaling stable blood sugar patterns, peak metabolic efficiency, and strong longevity potential.
        </div>
      </div>
      <div class="threshold-item bg-blue" v-else>
        <div class="threshold-text">
          <span class="label">Low </span>
          <span class="value">&lt; 4.8% (&lt; 94 mg/dL)</span>
        </div>
        <div class="description">
          Your A1C is in a low range—this may indicate a risk of hypoglycemia; please consult your healthcare provider for personalized advice.
        </div>
      </div>
    </div>

    <div class="footer-link">
      <a href="/learn-more" class="btn-link">
        Learn more
      </a>
    </div>
  </div>
</template>

<style scoped>

.result-primary {
  position: relative;
}

.result-value-wrapper {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
}

.segment {
  width: 19.2%;
  transition: flex 200ms ease;
  margin-right: 1%;
  &:last-child {
    margin-right: 0;
  }

  .segment-bg {
    width: 100%;
    height: 100%;
  }
}

.red {
  background: #e74c3c;
}

.yellow {
  background: #f1c40f;
}

.light-green {
  background: #b2e061;
}

.green {
  background: #27ae60;
}

.blue {
  background: #3498db;
}

.bg-red {
  background-color: #fdecea;
}

.bg-yellow {
  background-color: #fff9db;
}

.bg-light-green {
  background-color: #f4f9e8;
}

.bg-green {
  background-color: #e6f4ea;
}

.bg-blue {
  background-color: #e8f0fa;
}
</style>
